<template>
    <div class="editor-ct" :class="!editable ? 'read-only' : null">
        <div class="html-toolbar">
            <template v-if="editable">
                <el-button-group v-if="extensions['undo-redo']">
                    <el-button type="primary" size="mini" @click="execCmd('undo')"
                        ><i class="fa fa-undo"></i
                    ></el-button>
                    <el-button type="primary" size="mini" @click="execCmd('redo')"
                        ><i class="fa fa-repeat"></i
                    ></el-button>
                </el-button-group>
                <el-button-group v-if="extensions.text">
                    <el-button
                        v-if="extensions.text.bold"
                        type="primary"
                        size="mini"
                        :class="isActive('bold') ? 'active' : null"
                        @click="execCmd('toggleBold')"
                    >
                        <i class="fa fa-bold"></i>
                    </el-button>
                    <el-button
                        v-if="extensions.text.italic"
                        type="primary"
                        size="mini"
                        :class="isActive('italic') ? 'active' : null"
                        @click="execCmd('toggleItalic')"
                    >
                        <i class="fa fa-italic"></i>
                    </el-button>
                    <el-button
                        v-if="extensions.text.underline"
                        type="primary"
                        size="mini"
                        :class="isActive('underline') ? 'active' : null"
                        @click="execCmd('toggleUnderline')"
                    >
                        <i class="fa fa-underline"></i>
                    </el-button>
                    <el-button
                        v-if="extensions.text.strike"
                        type="primary"
                        size="mini"
                        :class="isActive('strike') ? 'active' : null"
                        @click="execCmd('toggleStrike')"
                    >
                        <i class="fa fa-strikethrough"></i>
                    </el-button>
                    <el-button
                        v-if="extensions.text.superscript"
                        type="primary"
                        size="mini"
                        :class="isActive('superscript') ? 'active' : null"
                        @click="execCmd('toggleSuperscript')"
                    >
                        <i class="fa fa-superscript"></i>
                    </el-button>
                    <el-button
                        v-if="extensions.text.subscript"
                        type="primary"
                        size="mini"
                        :class="isActive('subscript') ? 'active' : null"
                        @click="execCmd('toggleSubscript')"
                    >
                        <i class="fa fa-subscript"></i>
                    </el-button>
                </el-button-group>
                <el-select
                    v-if="extensions.textsize"
                    v-model="selectedTextSize"
                    size="mini"
                    class="w-10r"
                >
                    <el-option
                        v-for="item in textSizes"
                        :key="item.label"
                        :label="item.label"
                        :value="item.value"
                    >
                    </el-option>
                </el-select>
                <el-button-group v-if="extensions.list">
                    <el-button
                        v-if="extensions.list.unordered"
                        type="primary"
                        size="mini"
                        :class="isActive('bulletList') ? 'active' : null"
                        @click="execCmd('toggleBulletList')"
                    >
                        <i class="fa fa-list-ul"></i>
                    </el-button>
                    <el-button
                        v-if="extensions.list.ordered"
                        type="primary"
                        size="mini"
                        :class="isActive('orderedList') ? 'active' : null"
                        @click="execCmd('toggleOrderedList')"
                    >
                        <i class="fa fa-list-ol"></i>
                    </el-button>
                </el-button-group>
                <el-button-group v-if="extensions.textalign">
                    <el-button
                        v-if="extensions.textalign.left"
                        type="primary"
                        size="mini"
                        :class="isActive({ textAlign: 'left' }) ? 'active' : null"
                        @click="execCmd('setTextAlign', 'left')"
                    >
                        <i class="fa fa-align-left"></i>
                    </el-button>
                    <el-button
                        v-if="extensions.textalign.center"
                        type="primary"
                        size="mini"
                        :class="isActive({ textAlign: 'center' }) ? 'active' : null"
                        @click="execCmd('setTextAlign', 'center')"
                    >
                        <i class="fa fa-align-center"></i>
                    </el-button>
                    <el-button
                        v-if="extensions.textalign.right"
                        type="primary"
                        size="mini"
                        :class="isActive({ textAlign: 'right' }) ? 'active' : null"
                        @click="execCmd('setTextAlign', 'right')"
                    >
                        <i class="fa fa-align-right"></i>
                    </el-button>
                    <el-button
                        v-if="extensions.textalign.justify"
                        type="primary"
                        size="mini"
                        :class="isActive({ textAlign: 'justify' }) ? 'active' : null"
                        @click="execCmd('setTextAlign', 'justify')"
                    >
                        <i class="fa fa-align-justify"></i>
                    </el-button>
                </el-button-group>
                <el-popover
                    class="table-config"
                    placement="bottom"
                    width="100"
                    trigger="manual"
                    v-model="tableMenuVisible"
                >
                    <el-button
                        slot="reference"
                        @click="tableMenuVisible = !tableMenuVisible"
                        type="primary"
                        size="mini"
                    >
                        {{ $t('Table') }}
                    </el-button>
                    <div>
                        <div class="text-align-center">
                            {{ activeTableMatrixText.rowText }} x
                            {{ activeTableMatrixText.colText }}
                        </div>
                        <table ref="tableConfig" style="margin: 0px auto">
                            <tr v-for="row in tableRowCount" :key="`${row}-row`">
                                <td
                                    v-for="cell in tableColCount"
                                    :key="`${cell}-cell`"
                                    style="width: 20px; height: 20px"
                                >
                                    <div
                                        class="table-config-cell"
                                        @mouseenter="onTableCellMouseEnter($event, row, cell)"
                                        @click="onTableCellClick()"
                                    ></div>
                                </td>
                            </tr>
                        </table>
                    </div>
                </el-popover>
            </template>
        </div>
        <div class="editor-comp" @click="editor.chain().focus()">
            <editor-content :editor="editor" />
        </div>
    </div>
</template>

<script>
/* eslint-disable */

import FormQuestion from '@/data/FormQuestion';
import { Editor, EditorContent } from '@tiptap/vue-2';

import Bold from '@tiptap/extension-bold'
import Italic from '@tiptap/extension-italic'
import Strike from '@tiptap/extension-strike'
import Underline from '@tiptap/extension-underline'
import History from '@tiptap/extension-history'
import ListItem from '@tiptap/extension-list-item'
import BulletList from '@tiptap/extension-bullet-list'
import OrderedList from '@tiptap/extension-ordered-list'
import TextAlign from '@tiptap/extension-text-align'
import Heading from '@tiptap/extension-heading'
import Document from '@tiptap/extension-document'
import Paragraph from '@tiptap/extension-paragraph'
import Text from '@tiptap/extension-text'
import Table from '@tiptap/extension-table'
import TableCell from '@tiptap/extension-table-cell'
import TableHeader from '@tiptap/extension-table-header'
import TableRow from '@tiptap/extension-table-row'
import Superscript from '@tiptap/extension-superscript'
import Subscript from '@tiptap/extension-subscript'

const extensionMap = {
    'undo-redo': true,
    text: {
        bold: true,
        italic: true,
        underline: true,
        strike: true,
        superscript: true,
        subscript: true,
        link: true
    },
    textsize: {
        H1: true, H2: true, H3: true, H4: true, H5: true, H6: true
    },
    list: {
        ordered: true,
        unordered: true
    },
    textalign: {
        left: true,
        center: true,
        right: true,
        justify: true
    },
    table: true,
}

const tableCell = 'table-config-cell';

export default {
    props: {
        value: null,
        question: {
            type: FormQuestion,
            required: false,
        },
        editable: {
            type: Boolean,
            default: true,
        },
        extensions: {
            default () {
                return extensionMap
            }
        }
    },
    components: {
        EditorContent,
    },
    data () {
        return {
            editor: null,
            tableMenuVisible: false,
            tableRowCount: [1, 2, 3, 4, 5],
            tableColCount: [1, 2, 3, 4, 5],
            activeSelectedMatrixText: [],
            selectedTextSize: null
        }
    },
    mounted () {
        this.editor = new Editor({
            extensions: [
                Document, Paragraph, Text, Bold, Italic, Underline, Strike, ListItem, OrderedList, BulletList, History, Superscript, Subscript,
                Heading.configure({
                    levels: [1, 2, 3, 4, 5, 6],
                }),
                Table.configure({
                    resizable: true,
                }),
                TableCell, TableRow, TableHeader,
                TextAlign.configure({
                    types: ['heading', 'paragraph'],
                    alignments: ['left', 'center', 'right', 'justify'],
                })
            ],
            content: this.value,
            editable: this.editable,
            onUpdate: () => {
                const html = this.editor.getHTML()
                this.$emit('input', html);
            },
        })
    },
    computed: {
        textSizes () {
            const sizes = Object.keys(this.extensions.textsize)
            return sizes.map(item => {
                let value = item[1];
                return {
                    label: item,
                    value: parseInt(value)
                }
            })
        },
        activeTableMatrixText () {
            const [rowText, colText] = this.activeSelectedMatrixText
            return { rowText: rowText || 0, colText: colText || 0 };
        }
    },
    watch: {
        value (value) {
            const isSame = this.editor.getHTML() === value
            if (isSame) {
                return
            }

            this.editor.commands.setContent(value, false)
        },
        tableMenuVisible () {
            this.activeSelectedMatrixText = [];
            this.markAllTableConfigCellInActive();
        },
        selectedTextSize (value) {
            if (value) {
                this.execCmd('toggleHeading', { level: value })
            } else {
                this.execCmd('toggleHeading')
            }
        }
    },
    methods: {
        markAllTableConfigCellInActive () {
            const allCells = document.getElementsByClassName(tableCell);

            Array.prototype.forEach.call(allCells, elem => {
                elem.className = tableCell;
            })
        },
        onTableCellMouseEnter (evt, row, cell) {
            const rowIndex = row - 1,
                cellIndex = cell - 1,
                rows = this.$refs.tableConfig.rows,
                forEach = Array.prototype.forEach;

            this.markAllTableConfigCellInActive();

            forEach.call(rows, (rowItem, rowI) => {
                if (rowI <= rowIndex) {
                    const cells = rowItem.cells;
                    forEach.call(cells, (cellItem, cellI) => {
                        if (cellI <= cellIndex) {
                            cellItem.firstChild.className += ' active';
                        } else {
                            cellItem.firstChild.className = tableCell;
                        }
                    })
                }
            })

            this.activeSelectedMatrixText = [row, cell]
        },
        onTableCellClick () {
            const [rows, cols] = this.activeSelectedMatrixText;

            this.execCmd('insertTable', { rows: rows, cols: cols, withHeaderRow: true });
            this.tableMenuVisible = false;
        },
        isActive (type, params) {
            return this.editor ? this.editor.isActive(type, params) : false;
        },
        execCmd (fnName, params) {
            this.editor.chain().focus()[fnName](params).run();
        },
    }
}
</script>
<style lang="scss">
    @import "./styles.scss";
</style>
